/*
 * entrance-gate.js — full-screen entrance OVERLAY.
 *
 * The section covers its page until the visitor clicks ENTER, then it
 * animates out and is dismissed to reveal the real page underneath (no
 * navigation). On exit the arch zooms through and the logo hands off to the
 * page's actual header logo for a seamless transition into the site.
 *
 * Scroll-lock + "already seen" hiding are handled before paint by an inline
 * script in the section (sets html.cr-entrance-locked / .cr-entrance-skip).
 * In the theme editor the overlay shows on load and when its section is
 * selected, and ENTER simply dismisses it so the rest of the page is editable.
 */
if (!customElements.get('entrance-gate')) {
  customElements.define(
    'entrance-gate',
    class EntranceGate extends HTMLElement {
      connectedCallback() {
        this.inEditor = !!(window.Shopify && window.Shopify.designMode);

        // Portal the overlay to <body> so it sits above ALL page content,
        // free of any section stacking context. (Live only — in the editor
        // keep it in place so section selection works.) Re-entry guard: moving
        // re-runs connectedCallback with the element now a child of body.
        if (!this.inEditor && this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }

        this.enter = this.querySelector('.entrance__enter');
        this.scene = this.querySelector('.entrance__scene');
        this.logo = this.querySelector('.entrance__logo');

        if (this.enter) {
          this.onEnter = this.onEnter.bind(this);
          this.enter.addEventListener('click', this.onEnter);
        }

        if (this.inEditor) {
          this.section = this.closest('.shopify-section');
          this.onSelect = this.onSelect.bind(this);
          this.onDeselect = this.onDeselect.bind(this);
          document.addEventListener('shopify:section:select', this.onSelect);
          document.addEventListener('shopify:section:load', this.onSelect);
          document.addEventListener('shopify:section:deselect', this.onDeselect);
          // Start DISMISSED in the editor so the rest of the page is editable
          // — only show the gate when its section is selected.
          this.dismiss();
        }
      }

      disconnectedCallback() {
        this.enter?.removeEventListener('click', this.onEnter);
        if (this.onSelect) {
          document.removeEventListener('shopify:section:select', this.onSelect);
          document.removeEventListener('shopify:section:load', this.onSelect);
          document.removeEventListener('shopify:section:deselect', this.onDeselect);
        }
      }

      isMine(event) {
        return (
          this.section && 'shopify-section-' + event.detail.sectionId === this.section.id
        );
      }

      onSelect(event) {
        if (this.isMine(event)) this.show();
      }
      onDeselect(event) {
        if (this.isMine(event)) this.dismiss();
      }

      lock() {
        document.documentElement.classList.add('cr-entrance-locked');
      }
      unlock() {
        document.documentElement.classList.remove('cr-entrance-locked');
      }

      show() {
        this._dismissed = false;
        this.classList.remove('is-dismissed', 'is-leaving', 'is-fading');
        // Hide the header/nav until the entrance is dismissed.
        document.documentElement.classList.add('cr-entrance-active');
        // Don't lock scroll in the editor — the merchant needs to scroll/edit.
        if (!this.inEditor) this.lock();
      }

      dismiss() {
        if (this._dismissed) return;
        this._dismissed = true;
        this.classList.add('is-dismissed');
        // Reveal the header/nav now that the entrance is fully gone.
        document.documentElement.classList.remove('cr-entrance-active');
        this.unlock();
        if (this._flyingLogo) {
          this._flyingLogo.remove();
          this._flyingLogo = null;
        }
        if (!this.inEditor) this.setSeen();
      }

      setSeen() {
        const mode = this.dataset.frequency;
        if (mode === 'every') return;
        try {
          const store = mode === 'session' ? window.sessionStorage : window.localStorage;
          store.setItem('cr_entrance_seen', '1');
        } catch (e) {
          /* storage unavailable — show every time, no-op */
        }
      }

      onEnter(event) {
        event.preventDefault();
        if (this.classList.contains('is-leaving')) return;

        const animate = this.classList.contains('entrance--animate');
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Editor / no-animation / reduced-motion: just reveal the page.
        if (this.inEditor || !animate || reduceMotion) {
          this.dismiss();
          return;
        }

        this.classList.add('is-leaving');
        const pinned = this.pinLogo();

        // Sequence: arch zooms past -> logo glides to the nav (black stays
        // opaque, hiding the page so only the flying logo shows) -> ONLY THEN
        // fade the black out. This avoids ever seeing two logos at once.
        // Reveal the page only once the backdrop scene is decoded (so it never
        // pops in), but cap the wait so the reveal can't stall. The arch zoom +
        // logo glide are already playing as feedback, and the backdrop has been
        // preloading since page load, so in practice this resolves instantly —
        // the cap only matters on a cold cache + very slow network.
        const reveal = () => this.whenBackdropReady(() => this.fadeOut());
        const afterArch = () => {
          if (pinned) {
            this.moveLogoToHeader(reveal);
          } else {
            reveal();
          }
        };

        if (this.scene) {
          this.scene.addEventListener('animationend', afterArch, { once: true });
        } else {
          setTimeout(afterArch, 1100);
        }
        setTimeout(() => this.dismiss(), 3500); // overall safety net
      }

      // Run `done` once the backdrop scene image is decoded, or after a hard
      // cap (so the reveal can never stall). The backdrop is painted as a CSS
      // background, so we probe the same URL with an Image() — it was preloaded,
      // so this almost always resolves from cache immediately. Defensive: if
      // there's no backdrop or anything goes wrong, reveal right away.
      whenBackdropReady(done) {
        const CAP_MS = 500;
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          done();
        };
        const timer = setTimeout(finish, CAP_MS);
        const ready = () => {
          clearTimeout(timer);
          finish();
        };
        try {
          const fill = document.querySelector('.cr-fill');
          const bg = fill && getComputedStyle(fill).backgroundImage;
          const match = bg && bg.match(/url\(["']?(.*?)["']?\)/);
          if (!match || !match[1]) {
            ready();
            return;
          }
          const img = new Image();
          img.src = match[1];
          if (img.decode) {
            img.decode().then(ready, ready);
          } else if (img.complete) {
            ready();
          } else {
            img.addEventListener('load', ready, { once: true });
            img.addEventListener('error', ready, { once: true });
          }
        } catch (e) {
          ready();
        }
      }

      // Fade the overlay (and its black background) out, then remove it.
      // Reveal the header now so it cross-fades IN as the overlay fades OUT
      // (the flying logo stays on top until dismiss, so the hand-off is clean).
      fadeOut() {
        if (this.classList.contains('is-fading')) return;
        document.documentElement.classList.remove('cr-entrance-active');
        this.classList.add('is-fading');
        const onEnd = (event) => {
          if (event.target !== this || event.propertyName !== 'opacity') return;
          this.removeEventListener('transitionend', onEnd);
          this.dismiss();
        };
        this.addEventListener('transitionend', onEnd);
        setTimeout(() => {
          this.removeEventListener('transitionend', onEnd);
          this.dismiss();
        }, 1100); // fallback
      }

      // Detach the logo IMAGE and pin it (above the overlay) so the arch's
      // zoom doesn't drag it and it stays put while the arch passes. We fly
      // the <img> itself (not the wrapper) so it lands exactly on the header
      // logo image.
      pinLogo() {
        const img = this.logo && this.logo.querySelector('img');
        if (!img) return false;
        const first = img.getBoundingClientRect();
        if (!first.width) return false;
        this._logoFirst = first;

        img.style.position = 'fixed';
        img.style.margin = '0';
        img.style.top = first.top + 'px';
        img.style.left = first.left + 'px';
        img.style.width = first.width + 'px';
        img.style.height = first.height + 'px';
        img.style.maxWidth = 'none';
        img.style.transform = 'none';
        img.style.transformOrigin = 'top left';
        img.style.zIndex = '10000'; // above the overlay (9999)
        img.style.willChange = 'transform';
        document.body.appendChild(img);
        this._flyingLogo = img;
        return true;
      }

      // Glide the pinned logo image onto the page's REAL header logo IMAGE
      // (measured live), so when the overlay is dismissed it lines up exactly.
      moveLogoToHeader(onDone) {
        const logo = this._flyingLogo;
        const first = this._logoFirst;
        if (!logo || !first) {
          if (typeof onDone === 'function') onDone();
          return;
        }

        const headerImg = document.querySelector('.cr-header__logo img');
        let targetLeft = 32;
        let targetTop = 20;
        let scale = 140 / first.width;
        if (headerImg) {
          const tr = headerImg.getBoundingClientRect();
          if (tr.width) {
            targetLeft = tr.left;
            targetTop = tr.top;
            scale = tr.width / first.width;
          }
        }

        logo.style.transition = 'transform 0.85s cubic-bezier(0.5, 0, 0.2, 1)';
        void logo.offsetWidth;
        logo.style.transform =
          'translate(' + (targetLeft - first.left) + 'px, ' + (targetTop - first.top) + 'px) scale(' + scale + ')';

        // Run the callback once the logo has reached its final spot.
        if (typeof onDone === 'function') {
          let called = false;
          const fire = () => {
            if (called) return;
            called = true;
            logo.removeEventListener('transitionend', onTransitionEnd);
            onDone();
          };
          const onTransitionEnd = (event) => {
            if (event.target === logo && event.propertyName === 'transform') fire();
          };
          logo.addEventListener('transitionend', onTransitionEnd);
          setTimeout(fire, 1000); // fallback
        }
      }
    }
  );
}
